import React from 'react';

/**
 * LazyImage - renders images with:
 *  - Native lazy loading (loading="lazy")
 *  - WebP source via <picture> with original format as fallback
 *  - Error fallback to no_image.png
 */
function LazyImage({
  src,
  alt = "",
  className = "",
  loading = "lazy",
  style,
  onError: externalOnError,
  ...props
}) {
  // Derive a WebP src from the original src when it's a local asset
  const getWebpSrc = (originalSrc) => {
    if (!originalSrc || typeof originalSrc !== 'string') return null;
    // Only convert local asset paths (not external URLs or data URIs)
    if (originalSrc?.startsWith('http') || originalSrc?.startsWith('data:')) return null;
    // Strip query params for extension check
    const cleanPath = originalSrc?.split('?')?.[0];
    // Only convert known raster formats
    if (/\.(jpg|jpeg|png|gif)$/i?.test(cleanPath)) {
      const qs = originalSrc?.includes('?') ? '?' + originalSrc?.split('?')?.[1] : '';
      return cleanPath?.replace(/\.(jpg|jpeg|png|gif)$/i, '.webp') + qs;
    }
    return null;
  };

  const webpSrc = getWebpSrc(src);

  const handleError = (e) => {
    e.target.src = "/assets/images/no_image.png";
    if (externalOnError) externalOnError(e);
  };

  if (webpSrc) {
    return (
      <picture>
        <source srcSet={webpSrc} type="image/webp" />
        <img
          src={src}
          alt={alt}
          className={className}
          loading={loading}
          style={style}
          onError={handleError}
          {...props}
        />
      </picture>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      style={style}
      onError={handleError}
      {...props}
    />
  );
}

export default LazyImage;
